import { useEffect, useRef, useState } from 'react';

import {
  DEFAULT_GAME_CONTENT_SIZE,
  getScaleToFit,
  isValidContentSize,
  measureIframeContentSize,
  parseGameHtmlDimensions,
  resolveGameContentSize,
  Size,
} from './scaleToFit';

interface UseScaledGamePreviewOptions {
  initialContentSize?: Size;
  tridionHtmlContent?: string;
  url?: string;
}

/**
 * Scales game preview iframes to fit a fixed aspect-ratio frame without scrollbars.
 *
 * Tridion games are fixed-size e-learning exports (not responsive). Parent CSS can
 * only size the iframe element — it cannot shrink the game document inside a
 * cross-origin frame. Each game also ships at a different native resolution, so a
 * single hardcoded scale or aspect ratio breaks other titles.
 *
 * We resolve the game's native dimensions from Tridion metadata or by fetching its
 * index HTML, then compute scale = min(containerW / contentW, containerH / contentH)
 * and apply it via transform. A Storyline default size is used immediately so the
 * preview never blocks on fetch; live iframe measurement refines same-origin games.
 */
export const useScaledGamePreview = ({
  initialContentSize,
  tridionHtmlContent,
  url,
}: UseScaledGamePreviewOptions) => {
  const tridionParsedSize = tridionHtmlContent
    ? parseGameHtmlDimensions(tridionHtmlContent)
    : undefined;
  const seededContentSize = isValidContentSize(initialContentSize)
    ? initialContentSize
    : (tridionParsedSize ?? undefined);

  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fetchGenerationRef = useRef(0);
  const [containerSize, setContainerSize] = useState<Size>();
  const [resolvedContentSize, setResolvedContentSize] = useState<Size>();
  const [resolvedForUrl, setResolvedForUrl] = useState<string>();

  const contentSize = (() => {
    if (!url) {
      return undefined;
    }

    if (isValidContentSize(seededContentSize)) {
      return seededContentSize;
    }

    if (resolvedForUrl === url && isValidContentSize(resolvedContentSize)) {
      return resolvedContentSize;
    }

    return DEFAULT_GAME_CONTENT_SIZE;
  })();

  useEffect(() => {
    if (!url || isValidContentSize(seededContentSize)) {
      return;
    }

    const fetchGeneration = ++fetchGenerationRef.current;

    void (async () => {
      const resolvedSize = await resolveGameContentSize(url, seededContentSize);

      if (fetchGeneration !== fetchGenerationRef.current) {
        return;
      }

      setResolvedForUrl(url);
      setResolvedContentSize(resolvedSize);
    })();
  }, [seededContentSize, url]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const { width, height } = entry.contentRect;

      if (width > 0 && height > 0) {
        setContainerSize({ width, height });
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [url]);

  const scale =
    isValidContentSize(containerSize) && isValidContentSize(contentSize)
      ? getScaleToFit(containerSize, contentSize)
      : 1;

  const onIframeLoad = () => {
    const iframe = iframeRef.current;

    if (!iframe || !url || isValidContentSize(seededContentSize)) {
      return;
    }

    const measured = measureIframeContentSize(iframe);

    if (!isValidContentSize(measured)) {
      return;
    }

    setResolvedForUrl(url);
    setResolvedContentSize(current =>
      current?.width === measured.width && current.height === measured.height
        ? current
        : measured,
    );
  };

  return {
    containerRef,
    iframeRef,
    contentSize,
    scale,
    onIframeLoad,
  };
};
